import React from 'react'
import { useNavigate } from 'react-router-dom'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import type { Value as PhoneValue } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { LocationInput } from '../../components/ui'
import { Skeleton } from '../../components/ui/Skeleton'
import { apiGet, apiPut } from '../../lib/api'
import { BUSINESS, BUSINESS_CATEGORIES, BUSINESS_SCHEDULER_SLOTS, BUSINESS_TIMEZONES } from '../../lib/endpoints'
import { clearAuth, loadAuth } from '../../lib/authStorage'
import { AuthShell } from '../Auth/AuthShell'

type BusinessInfoResponse = {
  businessName?: string
  businessType?: string
  phone?: string
  location?: string
  timezone?: string
  preferredSlotId?: string
  preferredSlotCode?: string
  lapseWindowDays?: number
  dailyScanTime?: string
  emailGenerationInstructions?: string
  defaultOfferText?: string
  fromDisplayName?: string
  onboardingCompleted?: boolean
}

type SchedulerSlotResponse = {
  slotId: string
  timezone: string
  slotCode: string
  fireTimeLocal: string
  active: boolean
}

type SchedulerTimeZoneResponse = {
  id: string
  timezoneId: number
  timezone: string
}

type BusinessPayload = {
  businessName: string
  businessType: string
  phone: string
  location: string
  timezone: string
  preferredSlotId: string
  lapseWindowDays: number
  dailyScanTime: string
  emailGenerationInstructions: string
  defaultOfferText: string
  fromDisplayName: string
}

// ---------------------------------------------------------------------------
// Timezone helpers
// ---------------------------------------------------------------------------

function tzOffsetLabel(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    }).formatToParts(new Date())
    const offset = parts.find(p => p.type === 'timeZoneName')?.value ?? 'GMT'
    return offset === 'GMT' ? 'UTC+0' : offset.replace('GMT', 'UTC')
  } catch {
    return 'UTC'
  }
}

function tzOffsetMinutes(tz: string): number {
  try {
    const now = new Date()
    const utc = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }))
    const local = new Date(now.toLocaleString('en-US', { timeZone: tz }))
    return (local.getTime() - utc.getTime()) / 60000
  } catch {
    return 0
  }
}

function tzDisplayLabel(tz: string): string {
  const city = tz.split('/').pop()?.replace(/_/g, ' ') ?? tz
  return `(${tzOffsetLabel(tz)}) ${city}`
}

// ---------------------------------------------------------------------------
// TimezoneSelect — searchable combobox
// ---------------------------------------------------------------------------

type TzOption = { id: string; timezone: string }

function TimezoneSelect({
  value,
  options,
  loading,
  onChange,
}: {
  value: string
  options: TzOption[]
  loading: boolean
  onChange: (tz: string) => void
}) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')
  const containerRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const listRef = React.useRef<HTMLDivElement>(null)

  // Sort once by offset then alpha
  const sorted = React.useMemo(() => {
    return [...options].sort((a, b) => {
      const diff = tzOffsetMinutes(a.timezone) - tzOffsetMinutes(b.timezone)
      return diff !== 0 ? diff : a.timezone.localeCompare(b.timezone)
    })
  }, [options])

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return sorted
    return sorted.filter(o =>
      o.timezone.toLowerCase().includes(q) ||
      tzDisplayLabel(o.timezone).toLowerCase().includes(q)
    )
  }, [sorted, query])

  // Close on outside click
  React.useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setQuery('')
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [])

  // Scroll selected item into view when opening
  React.useEffect(() => {
    if (open && listRef.current) {
      const selected = listRef.current.querySelector('[data-selected="true"]')
      selected?.scrollIntoView({ block: 'nearest' })
    }
  }, [open])

  function select(tz: string) {
    onChange(tz)
    setOpen(false)
    setQuery('')
  }

  const displayValue = value ? tzDisplayLabel(value) : ''

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      {/* Input */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          border: open ? '1px solid var(--accent)' : '1px solid var(--border)',
          borderRadius: 10,
          background: 'var(--surface)',
          padding: '0 12px',
          height: 44,
          gap: 8,
          cursor: 'text',
          transition: 'border-color 0.15s',
        }}
        onClick={() => {
          setOpen(true)
          setQuery('')
          setTimeout(() => inputRef.current?.focus(), 0)
        }}
      >
        <span style={{ fontSize: 15, flexShrink: 0 }}>🌍</span>
        <input
          ref={inputRef}
          value={open ? query : displayValue}
          placeholder={loading ? 'Loading timezones…' : 'Search timezone…'}
          readOnly={!open}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => { setOpen(true); setQuery('') }}
          style={{
            flex: 1,
            border: 'none',
            background: 'transparent',
            fontSize: 14,
            color: 'var(--text)',
            outline: 'none',
            cursor: open ? 'text' : 'pointer',
            minWidth: 0,
          }}
        />
        <span style={{ fontSize: 11, color: 'var(--text3)', pointerEvents: 'none', flexShrink: 0 }}>
          {open ? '▲' : '▼'}
        </span>
      </div>

      {/* Dropdown */}
      {open && (
        <div
          ref={listRef}
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 100,
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          {filtered.length === 0 ? (
            <div style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text3)' }}>
              No timezones found
            </div>
          ) : (
            filtered.map(o => {
              const isSelected = o.timezone === value
              return (
                <div
                  key={o.id}
                  data-selected={isSelected}
                  onMouseDown={() => select(o.timezone)}
                  style={{
                    padding: '9px 16px',
                    fontSize: 13.5,
                    cursor: 'pointer',
                    background: isSelected ? 'var(--accent-bg)' : 'transparent',
                    color: isSelected ? 'var(--accent)' : 'var(--text)',
                    fontWeight: isSelected ? 600 : 400,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                  }}
                  onMouseEnter={e => {
                    if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'var(--surface2)'
                  }}
                  onMouseLeave={e => {
                    if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'transparent'
                  }}
                >
                  <span>{tzDisplayLabel(o.timezone)}</span>
                  {isSelected && <span style={{ fontSize: 12 }}>✓</span>}
                </div>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}

type BusinessCategory = {
  id: string
  name: string
  emoji: string
  description: string
}

const LAPSE_OPTIONS = [
  { days: 14, title: 'High frequency', hint: 'Frequent visitors' },
  { days: 21, title: 'Balanced cadence', hint: 'Common mid-range window' },
  { days: 30, title: 'Less frequent', hint: 'Longer cycles' },
] as const

export default function BusinessOnboarding() {
  const navigate = useNavigate()
  const auth = loadAuth()
  const token = auth?.accessToken

  const [step, setStep] = React.useState(1)
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [touched, setTouched] = React.useState<Record<string, boolean>>({})
  const [searchQuery, setSearchQuery] = React.useState('')
  const [businessCategories, setBusinessCategories] = React.useState<BusinessCategory[]>([])
  const [categoriesLoading, setCategoriesLoading] = React.useState(true)
  const [availableTimezones, setAvailableTimezones] = React.useState<SchedulerTimeZoneResponse[]>([])
  const [timezonesLoading, setTimezonesLoading] = React.useState(false)
  const [timezonesError, setTimezonesError] = React.useState<string | null>(null)
  const [schedulerSlots, setSchedulerSlots] = React.useState<SchedulerSlotResponse[]>([])
  const [slotsLoading, setSlotsLoading] = React.useState(false)
  const [slotsError, setSlotsError] = React.useState<string | null>(null)
  const [phoneValue, setPhoneValue] = React.useState<PhoneValue | undefined>(undefined)
  const [manualTypeEnabled, setManualTypeEnabled] = React.useState(false)
  const [manualBusinessType, setManualBusinessType] = React.useState('')
  const [customLapseEnabled, setCustomLapseEnabled] = React.useState(false)
  const [customLapseDays, setCustomLapseDays] = React.useState('')
  const [form, setForm] = React.useState<BusinessPayload>({
    businessName: '',
    businessType: '',
    phone: '',
    location: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    preferredSlotId: '',
    lapseWindowDays: 21,
    dailyScanTime: '9:00 AM',
    emailGenerationInstructions: '',
    defaultOfferText: '',
    fromDisplayName: '',
  })

  React.useEffect(() => {
    if (!token) {
      navigate('/signin', { replace: true })
      return
    }
    async function loadExisting() {
      setLoading(true)
      setError(null)
      try {
        const data = await apiGet<BusinessInfoResponse>(BUSINESS)
        if (data.onboardingCompleted) {
          navigate('/dashboard', { replace: true })
          return
        }
        setForm(prev => ({
          ...prev,
          businessName: data.businessName ?? prev.businessName,
          businessType: data.businessType ?? prev.businessType,
          phone: data.phone ?? prev.phone,
          location: data.location ?? prev.location,
          timezone: data.timezone ?? prev.timezone,
          preferredSlotId: data.preferredSlotId ?? prev.preferredSlotId,
          lapseWindowDays: data.lapseWindowDays ?? prev.lapseWindowDays,
          dailyScanTime: data.dailyScanTime ?? prev.dailyScanTime,
          emailGenerationInstructions: data.emailGenerationInstructions ?? prev.emailGenerationInstructions,
          defaultOfferText: data.defaultOfferText ?? prev.defaultOfferText,
          fromDisplayName: data.fromDisplayName ?? prev.fromDisplayName,
        }))
        if (data.phone) setPhoneValue(data.phone as PhoneValue)
        if (data.lapseWindowDays && ![14, 21, 30].includes(data.lapseWindowDays)) {
          setCustomLapseEnabled(true)
          setCustomLapseDays(String(data.lapseWindowDays))
        }
        if (data.businessType && !businessCategories.some(c => c.name === data.businessType)) {
          setManualTypeEnabled(true)
          setManualBusinessType(data.businessType)
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load onboarding')
      } finally {
        setLoading(false)
      }
    }
    loadExisting()
  }, [navigate, token])

  React.useEffect(() => {
    if (!token || !form.timezone.trim()) return
    let cancelled = false
    async function loadSlots() {
      setSlotsLoading(true)
      setSlotsError(null)
      try {
        const slots = await apiGet<SchedulerSlotResponse[]>(
          BUSINESS_SCHEDULER_SLOTS(form.timezone.trim()),
        )
        if (cancelled) return
        setSchedulerSlots(slots)
        setForm(prev => {
          const hasSelected = slots.some(s => s.slotId === prev.preferredSlotId)
          if (hasSelected) return prev
          const first = slots[0]
          if (!first) return { ...prev, preferredSlotId: '', dailyScanTime: '' }
          return {
            ...prev,
            preferredSlotId: first.slotId,
            dailyScanTime: first.fireTimeLocal,
          }
        })
      } catch (e) {
        if (cancelled) return
        setSchedulerSlots([])
        setSlotsError(e instanceof Error ? e.message : 'Failed to load scheduler slots')
      } finally {
        if (!cancelled) setSlotsLoading(false)
      }
    }
    loadSlots()
    return () => {
      cancelled = true
    }
  }, [form.timezone, token])

  React.useEffect(() => {
    let cancelled = false
    async function loadCategories() {
      setCategoriesLoading(true)
      try {
        const cats = await apiGet<BusinessCategory[]>(BUSINESS_CATEGORIES)
        if (cancelled) return
        setBusinessCategories(cats)
        setForm(prev => ({
          ...prev,
          businessType: prev.businessType || (cats[0]?.name ?? ''),
        }))
      } catch {
        // silently ignore — user can still type manually
      } finally {
        if (!cancelled) setCategoriesLoading(false)
      }
    }
    loadCategories()
    return () => { cancelled = true }
  }, [])

  React.useEffect(() => {
    if (!token) return
    let cancelled = false
    async function loadTimezones() {
      setTimezonesLoading(true)
      setTimezonesError(null)
      try {
        const zones = await apiGet<SchedulerTimeZoneResponse[]>(BUSINESS_TIMEZONES)
        if (cancelled) return
        setAvailableTimezones(zones)
        setForm(prev => {
          if (zones.some(z => z.timezone === prev.timezone)) return prev
          return { ...prev, timezone: zones[0]?.timezone ?? prev.timezone }
        })
      } catch (e) {
        if (cancelled) return
        setTimezonesError(e instanceof Error ? e.message : 'Failed to load timezones')
      } finally {
        if (!cancelled) setTimezonesLoading(false)
      }
    }
    loadTimezones()
    return () => {
      cancelled = true
    }
  }, [token])

  const phoneValid = !!phoneValue && isValidPhoneNumber(phoneValue)

  function handlePhoneChange(value: PhoneValue | undefined) {
    setPhoneValue(value)
    update('phone', value ?? '')
  }
  const parsedCustomLapseDays = Number(customLapseDays)
  // 14-day floor matches the backend validator. Shorter windows hurt reply
  // rates and cost margin — owners with high-cadence businesses should use a
  // larger plan rather than tighten this.
  const customLapseValid =
    Number.isInteger(parsedCustomLapseDays) && parsedCustomLapseDays >= 14 && parsedCustomLapseDays <= 180
  const lapseValid = customLapseEnabled ? customLapseValid : [14, 21, 30].includes(form.lapseWindowDays)
  const resolvedBusinessType = manualTypeEnabled ? manualBusinessType.trim() : form.businessType.trim()
  const resolvedLapseWindowDays = customLapseEnabled ? parsedCustomLapseDays : form.lapseWindowDays

  const step1Valid = Boolean(resolvedBusinessType)
  const step2Valid = Boolean(
    form.businessName.trim() &&
      phoneValid &&
      form.location.trim() &&
      form.timezone.trim() &&
      form.preferredSlotId.trim() &&
      form.fromDisplayName.trim(),
  )
  const step3Valid = Boolean(lapseValid)

  const canContinue = (current: number) => {
    if (current === 1) return step1Valid
    if (current === 2) return step2Valid
    return step3Valid
  }

  const update = <K extends keyof BusinessPayload>(key: K, value: BusinessPayload[K]) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  const filteredBusinessTypes = businessCategories.filter(option => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return true
    return (
      option.name.toLowerCase().includes(q) ||
      option.description.toLowerCase().includes(q)
    )
  })

  const markStepTouched = (currentStep: number) => {
    if (currentStep === 1) {
      setTouched(prev => ({ ...prev, businessType: true }))
      return
    }
    if (currentStep === 2) {
      setTouched(prev => ({
        ...prev,
        businessName: true,
        phone: true,
        location: true,
        timezone: true,
        preferredSlotId: true,
        fromDisplayName: true,
      }))
      return
    }
    setTouched(prev => ({
      ...prev,
      lapseWindowDays: true,
    }))
  }

  async function submit() {
    if (!token) return
    if (!step1Valid || !step2Valid || !step3Valid) {
      markStepTouched(3)
      setError('Please complete all required fields.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await apiPut<BusinessInfoResponse, BusinessPayload>(
        BUSINESS,
        { ...form, businessType: resolvedBusinessType, lapseWindowDays: resolvedLapseWindowDays },
      )
      navigate('/dashboard', { replace: true })
    } catch (e) {
      if (e instanceof Error && e.message.includes('401')) {
        clearAuth()
        navigate('/signin', { replace: true })
        return
      }
      setError(e instanceof Error ? e.message : 'Failed to save onboarding')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg)' }}>
        <div style={{ width: 420, display: 'flex', flexDirection: 'column', gap: 20, padding: '40px 32px', background: 'var(--surface)', borderRadius: 16, border: '1px solid var(--border)' }} aria-hidden="true">
          <Skeleton height={12} width="35%" borderRadius={6} />
          <Skeleton height={26} width="70%" borderRadius={6} />
          <Skeleton height={14} width="55%" borderRadius={6} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 8 }}>
            {Array.from({ length: 3 }, (_, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <Skeleton height={11} width="28%" borderRadius={4} />
                <Skeleton height={38} width="100%" borderRadius={8} />
              </div>
            ))}
          </div>
          <Skeleton height={42} width="100%" borderRadius={8} style={{ marginTop: 4 }} />
        </div>
      </div>
    )
  }

  const stepTitles = [
    'Choose your business type',
    'Tell us about your business',
    'Set re-engagement preferences',
  ]
  const stepDescriptions = [
    'Nudge personalises messages based on your business type.',
    'We use this to personalise From name and business context.',
    'Choose when customers are considered lapsed for re-engagement.',
  ]
  const leftTipsByStep: Record<number, string[]> = {
    1: [
      'Pick the closest business category to improve AI tone.',
      'Use search to find a category quickly.',
      'If not listed, enable manual type and enter your own.',
    ],
    2: [
      'Display name appears in outgoing messages.',
      'Location/timezone helps timing and personalization.',
    ],
    3: [
      'Lapse window controls when a customer is flagged.',
      'You can use one of the presets or define your own window.',
      'You can change all these settings later in Business tab.',
      'After setup, import contacts with past visit dates — Nudge will start sending emails on day 1 of your trial.',
    ],
  }
  const selectShellStyle: React.CSSProperties = {
    position: 'relative',
    border: '1px solid var(--border)',
    borderRadius: 12,
    background: 'var(--surface2)',
    height: 42,
    display: 'flex',
    alignItems: 'center',
  }
  const selectControlStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    color: 'var(--text)',
    fontSize: 14,
    fontFamily: "'Geist', sans-serif",
    padding: '0 40px 0 12px',
    appearance: 'none',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
    cursor: 'pointer',
  }

  return (
    <AuthShell
      title="Business onboarding"
      subtitle="Complete this setup once to unlock your dashboard."
      compact
      narrowLeft
      leftTitle="Set up your business in minutes."
      leftSubtitle={`Step ${step} of 3 — ${stepTitles[step - 1]}`}
      leftTips={leftTipsByStep[step]}
    >
      <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
            {[1, 2, 3].map((s, index) => (
              <React.Fragment key={s}>
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    border: `2px solid ${s <= step ? 'var(--accent)' : 'var(--border)'}`,
                    background: s < step ? 'var(--accent)' : 'var(--surface2)',
                    color: s <= step ? (s < step ? 'var(--accent-text)' : 'var(--accent)') : 'var(--text3)',
                    display: 'grid',
                    placeItems: 'center',
                    fontWeight: 600,
                    fontSize: 14,
                  }}
                >
                  {s < step ? '✓' : s}
                </div>
                {index < 2 ? (
                  <div style={{ flex: 1, height: 2, background: s < step ? 'var(--accent)' : 'var(--border)' }} />
                ) : null}
              </React.Fragment>
            ))}
          </div>

          <div style={{ fontFamily: 'Fraunces, serif', fontSize: 34, lineHeight: 1.07, color: 'var(--text)' }}>
            {stepTitles[step - 1]}
          </div>
          <div style={{ marginTop: 10, color: 'var(--text2)', fontSize: 16, maxWidth: 760 }}>
            {stepDescriptions[step - 1]}
          </div>

          <div style={{ marginTop: 18 }}>
            {step === 1 ? (
              <div
                style={{
                  display: 'grid',
                  gap: 10,
                  maxHeight: '34vh',
                  overflowY: 'auto',
                  paddingRight: 4,
                }}
              >
                <div
                  style={{
                    borderRadius: 12,
                    background: 'var(--surface)',
                    border: '1px solid var(--border)',
                    padding: 10,
                    display: 'grid',
                    gap: 10,
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                  }}
                >
                  <div style={{ position: 'relative' }}>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        width: 14,
                        height: 14,
                        color: 'var(--text3)',
                        position: 'absolute',
                        left: 12,
                        top: '50%',
                        transform: 'translateY(-50%)',
                      }}
                    >
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                      className="form-input"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder="Search business type..."
                      style={{ paddingLeft: 34 }}
                    />
                  </div>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: 12,
                      color: 'var(--text2)',
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    <span>Set type manually</span>
                    <button
                      type="button"
                      onClick={() => setManualTypeEnabled(prev => !prev)}
                      aria-pressed={manualTypeEnabled}
                      aria-label="Toggle manual business type"
                      style={{
                        width: 42,
                        height: 24,
                        borderRadius: 999,
                        border: `1px solid ${manualTypeEnabled ? 'var(--accent)' : 'var(--border2)'}`,
                        background: manualTypeEnabled ? 'var(--accent)' : 'var(--surface2)',
                        position: 'relative',
                        transition: 'all 160ms ease',
                        cursor: 'pointer',
                      }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          top: 2,
                          left: manualTypeEnabled ? 20 : 2,
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          background: 'var(--surface)',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                          transition: 'left 160ms ease',
                        }}
                      />
                    </button>
                  </label>
                </div>

                {!manualTypeEnabled ? (
                  categoriesLoading ? (
                    <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text3)', fontSize: 14 }}>
                      Loading categories…
                    </div>
                  ) : filteredBusinessTypes.map(option => {
                  const active = form.businessType === option.name
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => update('businessType', option.name)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        textAlign: 'left',
                        width: '100%',
                        borderRadius: 14,
                        padding: '12px 14px',
                        border: active ? '1px solid var(--accent)' : '1px solid var(--border)',
                        background: active ? 'var(--accent-bg)' : 'var(--surface)',
                        cursor: 'pointer',
                        position: 'relative',
                      }}
                    >
                      <span
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 10,
                          display: 'grid',
                          placeItems: 'center',
                          background: active ? 'var(--accent-bg)' : 'var(--surface2)',
                          fontSize: 20,
                        }}
                      >
                        {option.emoji}
                      </span>
                      <span>
                        <div style={{ color: active ? 'var(--accent)' : 'var(--text)', fontSize: 16, fontWeight: 600, lineHeight: 1.15 }}>
                          {option.name}
                        </div>
                        <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 2 }}>{option.description}</div>
                      </span>
                      {active ? (
                        <span
                          style={{
                            position: 'absolute',
                            right: 12,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            background: 'var(--accent)',
                            color: 'var(--accent-text)',
                            display: 'grid',
                            placeItems: 'center',
                            fontSize: 14,
                            fontWeight: 700,
                          }}
                        >
                          ✓
                        </span>
                      ) : null}
                    </button>
                  )
                })
                ) : (
                  <div
                    style={{
                      borderRadius: 16,
                      background: 'var(--surface)',
                      border: '1px solid var(--border)',
                      padding: 16,
                    }}
                  >
                    <label className="form-label">Custom Business Type</label>
                    <input
                      className="form-input"
                      value={manualBusinessType}
                      maxLength={120}
                      placeholder="e.g. Real Estate Agency, Photography Studio, Marine Services"
                      onChange={e => setManualBusinessType(e.target.value)}
                    />
                    {touched.businessType && !manualBusinessType.trim() ? (
                      <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 6 }}>
                        Please enter your business type.
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            ) : null}

            {step === 2 ? (
              <div
                style={{
                  borderRadius: 16,
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  padding: 16,
                  display: 'grid',
                  gap: 14,
                }}
              >
                <div>
                  <label className="form-label">Business Name</label>
                  <input
                    className="form-input"
                    value={form.businessName}
                    onChange={e => update('businessName', e.target.value)}
                  />
                  {touched.businessName && !form.businessName.trim() ? (
                    <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 6 }}>Business name is required.</div>
                  ) : null}
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-3">
                  <div>
                    <label className="form-label">Phone</label>
                    <PhoneInput
                      international
                      defaultCountry="US"
                      value={phoneValue}
                      onChange={handlePhoneChange}
                      className={`form-phone${touched.phone && !phoneValid ? ' form-phone--error' : ''}`}
                    />
                    {touched.phone && !phoneValid ? (
                      <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 6 }}>Enter a valid phone number for the selected country.</div>
                    ) : null}
                  </div>
                  <div>
                    <label className="form-label">
                      From Display Name
                      <span style={{ fontWeight: 400, color: 'var(--text2)', marginLeft: 6, fontSize: 11 }}>— email sender name</span>
                    </label>
                    <input className="form-input" value={form.fromDisplayName} onChange={e => update('fromDisplayName', e.target.value)} />
                    {touched.fromDisplayName && !form.fromDisplayName.trim() && (
                      <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 4 }}>From display name is required.</div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-3">
                  <div>
                    <label className="form-label">Location</label>
                    <LocationInput className="form-input" value={form.location} onChange={v => update('location', v)} />
                    {touched.location && !form.location.trim() ? (
                      <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 6 }}>Location is required.</div>
                    ) : null}
                  </div>
                  <div>
                    <label className="form-label">Timezone</label>
                    <TimezoneSelect
                      value={form.timezone}
                      options={availableTimezones}
                      loading={timezonesLoading}
                      onChange={tz => update('timezone', tz)}
                    />
                    {touched.timezone && !form.timezone.trim() ? (
                      <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 6 }}>Timezone is required.</div>
                    ) : null}
                    {timezonesError ? (
                      <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 6 }}>{timezonesError}</div>
                    ) : null}
                  </div>
                </div>
                <div
                  style={{
                    borderRadius: 12,
                    background: 'var(--surface2)',
                    border: '1px solid var(--border)',
                    padding: '12px 14px',
                    color: 'var(--text2)',
                    fontSize: 13,
                  }}
                >
                  Your selected timezone determines when automated nudging windows run for your business.
                </div>
                <div>
                  <label className="form-label">Preferred Time to Nudge</label>
                  <div style={selectShellStyle}>
                    <select
                      value={form.preferredSlotId}
                      onChange={e => {
                        const slot = schedulerSlots.find(s => s.slotId === e.target.value)
                        update('preferredSlotId', e.target.value)
                        if (slot) update('dailyScanTime', slot.fireTimeLocal)
                      }}
                      disabled={slotsLoading}
                      style={selectControlStyle}
                    >
                      {slotsLoading ? <option>Loading available slots...</option> : null}
                      {!slotsLoading && schedulerSlots.length === 0 ? (
                        <option value="">No slots available for this timezone</option>
                      ) : null}
                      {!slotsLoading
                        ? schedulerSlots.map(slot => (
                            <option key={slot.slotId} value={slot.slotId}>
                              {slot.slotCode.replace('SLOT_', 'Slot ')} - {slot.fireTimeLocal.slice(0, 5)}
                            </option>
                          ))
                        : null}
                    </select>
                    <span
                      style={{
                        position: 'absolute',
                        right: 12,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--text3)',
                        pointerEvents: 'none',
                        fontSize: 12,
                      }}
                    >
                      ▼
                    </span>
                  </div>
                  {slotsError ? (
                    <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 6 }}>{slotsError}</div>
                  ) : null}
                  {touched.preferredSlotId && !form.preferredSlotId.trim() ? (
                    <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 6 }}>
                      Preferred nudge time is required.
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {step === 3 ? (
              <div style={{ display: 'grid', gap: 12, maxHeight: '34vh', overflowY: 'auto', paddingRight: 4 }}>
                <div className="grid grid-cols-2 gap-3 min-[520px]:grid-cols-4 min-[520px]:gap-3.5">
                  {LAPSE_OPTIONS.map(option => {
                    const active = !customLapseEnabled && form.lapseWindowDays === option.days
                    return (
                      <button
                        key={option.days}
                        type="button"
                        onClick={() => {
                          setCustomLapseEnabled(false)
                          setCustomLapseDays('')
                          update('lapseWindowDays', option.days)
                        }}
                        style={{
                          borderRadius: 16,
                          border: active ? '1px solid var(--accent)' : '1px solid var(--border)',
                          background: active ? 'var(--accent-bg)' : 'var(--surface)',
                          padding: '12px 10px',
                          textAlign: 'center',
                          cursor: 'pointer',
                        }}
                      >
                        <div style={{ fontSize: 34, color: active ? 'var(--accent)' : 'var(--text)', lineHeight: 1 }}>{option.days}</div>
                        <div style={{ color: 'var(--text2)', fontSize: 14, marginTop: 2 }}>days</div>
                        <div style={{ color: active ? 'var(--accent)' : 'var(--text2)', marginTop: 8, fontWeight: 600, fontSize: 14 }}>
                          {option.title}
                        </div>
                        <div style={{ color: 'var(--text3)', marginTop: 4, fontSize: 12 }}>{option.hint}</div>
                      </button>
                    )
                  })}
                  <button
                    type="button"
                    onClick={() => setCustomLapseEnabled(true)}
                    style={{
                      borderRadius: 16,
                      border: customLapseEnabled ? '1px solid var(--accent)' : '1px solid var(--border)',
                      background: customLapseEnabled ? 'var(--accent-bg)' : 'var(--surface)',
                      padding: '12px 10px',
                      textAlign: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontSize: 28, color: customLapseEnabled ? 'var(--accent)' : 'var(--text)', lineHeight: 1 }}>✎</div>
                    <div style={{ color: 'var(--text2)', fontSize: 14, marginTop: 2 }}>days</div>
                    <div style={{ color: customLapseEnabled ? 'var(--accent)' : 'var(--text2)', marginTop: 8, fontWeight: 600, fontSize: 14 }}>
                      Custom
                    </div>
                    <div style={{ color: 'var(--text3)', marginTop: 4, fontSize: 12 }}>14 - 180</div>
                  </button>
                </div>

                {customLapseEnabled ? (
                  <div
                    style={{
                      borderRadius: 16,
                      background: 'var(--surface)',
                      border: '1px solid var(--border)',
                      padding: 16,
                    }}
                  >
                    <label className="form-label">Custom Lapsed Window (days)</label>
                    <input
                      className="form-input"
                      type="number"
                      min={14}
                      max={180}
                      value={customLapseDays}
                      onChange={e => setCustomLapseDays(e.target.value)}
                      placeholder="Enter days between 14 and 180"
                    />
                    {touched.lapseWindowDays && !customLapseValid ? (
                      <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 6 }}>
                        Enter a whole number between 14 and 180.
                      </div>
                    ) : null}
                    <div style={{ color: 'var(--text3)', fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>
                      Windows shorter than 14 days tend to over-message customers
                      and lower reply rates. We recommend 21–30 days for most
                      businesses.
                    </div>
                  </div>
                ) : null}

                <div
                  style={{
                    borderRadius: 14,
                    border: '1px solid var(--border)',
                    background: 'var(--surface)',
                    padding: '12px 14px',
                    color: 'var(--text2)',
                    fontSize: 13,
                  }}
                >
                  💡 Most small businesses find <strong>60 days</strong> works best — long enough to avoid
                  annoying frequent customers, short enough to catch drift early.
                </div>

              </div>
            ) : null}
          </div>

          {error ? (
            <div style={{ marginTop: 14, color: 'var(--red)', fontSize: 13 }}>{error}</div>
          ) : null}

          <div
            className="mt-6 flex flex-wrap items-center justify-between gap-3"
            style={{ rowGap: 12 }}
          >
            <button
              className="btn-secondary order-1 sm:order-none"
              onClick={() => setStep(prev => Math.max(1, prev - 1))}
              disabled={step === 1 || saving}
              style={{ visibility: step === 1 ? 'hidden' : 'visible' }}
            >
              Back
            </button>
            <div
              className="order-3 w-full text-center sm:order-none sm:w-auto"
              style={{ color: 'var(--text3)', fontFamily: "'Geist Mono', monospace", fontSize: 12 }}
            >
              Step {step} of 3
            </div>
            {step < 3 ? (
              <button
                className="btn-primary order-2 sm:order-none"
                onClick={() => {
                  markStepTouched(step)
                  if (!canContinue(step)) return
                  setStep(prev => Math.min(3, prev + 1))
                }}
                disabled={saving}
              >
                Continue
              </button>
            ) : (
              <button
                className="btn-primary order-2 sm:order-none"
                onClick={submit}
                disabled={!canContinue(3) || saving}
              >
                {saving ? 'Saving...' : 'Complete Setup'}
              </button>
            )}
          </div>
      </div>
    </AuthShell>
  )
}

